import { createSlice, createAsyncThunk, createSelector } from "@reduxjs/toolkit";
import { fetchFilterDimension, fetchSegmentTree, fetchTerritoryTree } from "../api/filterDimensionsApi";
import {
  encodeSegmentNode,
  segmentLabel,
  subSegmentLabel,
} from "../constants/segments";
import { encodeTerritoryNode } from "../constants/territories";

// Default page sizes per dimension. Static dims (platforms, segment_types,
// price_levels) use a high limit so a single page returns the full set;
// paginated dims fetch in chunks of 50 with scroll-to-load-more handled
// by LazyDimensionSelect.
const DEFAULT_LIMITS = {
  platforms: 10000,
  segment_types: 10000,
  price_levels: 10000,
  franchises: 50,
  geo_level_1: 100,
  geo_level_2: 50,
  cities: 50,
  cuisines: 50,
  postal_codes: 50,
  crm_client_ids: 50,
  place_ids: 50,
};

const ALL_DIMENSIONS = Object.keys(DEFAULT_LIMITS);

const makeDimensionState = () => ({
  items: [],
  hasMore: false,
  page: 1,
  loading: false,
  loaded: false,
  searchTerm: "",
});

const initialState = {
  // Metadata about the current filter context
  meta: {
    id_client: null,
    country: null,
    refresh: null,
    initialized: false,
    image_audit_feature_enabled: true,
  },
  dimensions: Object.fromEntries(
    ALL_DIMENSIONS.map((dim) => [dim, makeDimensionState()])
  ),
  // The two-level segment hierarchy, kept apart from `dimensions` because its items
  // are parents carrying children rather than flat values.
  segmentTree: {
    items: [],
    loading: false,
    loaded: false,
  },
  // The client's sales regions with their territories, fetched only for a market
  // with sales territories. `error` separates "could not load" from "none".
  territoryTree: {
    items: [],
    loading: false,
    loaded: false,
    error: null,
  },
};

// ---------------------------------------------------------------------------
// Thunks
// ---------------------------------------------------------------------------

/**
 * Fetch one page of a single dimension. Appends items if page > 1 (load more).
 * Replaces items if page == 1 (initial load or after search reset).
 */
export const fetchFilterDimensionPage = createAsyncThunk(
  "filterDimensions/fetchPage",
  async (
    { dimension, page = 1, search, signal },
    { getState, rejectWithValue }
  ) => {
    const { meta } = getState().filterDimensions;
    const limit = DEFAULT_LIMITS[dimension] ?? 50;

    try {
      const data = await fetchFilterDimension({
        dimension,
        id_client: meta.id_client,
        country: meta.country,
        refresh: meta.refresh,
        page,
        limit,
        search,
        signal,
      });
      // The scope travels with the result so a response that lands after a workspace or
      // country switch can be told apart from one for the current scope.
      return { dimension, page, data, id_client: meta.id_client, country: meta.country };
    } catch (err) {
      if (err.name === "AbortError" || err.code === "ERR_CANCELED") {
        return rejectWithValue({ dimension, aborted: true });
      }
      return rejectWithValue({ dimension, error: err.message });
    }
  }
);

/**
 * Load the next page for a dimension that has more items.
 */
export const fetchMoreFilterDimension = createAsyncThunk(
  "filterDimensions/fetchMore",
  async ({ dimension }, { getState, dispatch }) => {
    const dimState = getState().filterDimensions.dimensions[dimension];
    if (!dimState.hasMore || dimState.loading) return;

    const nextPage = dimState.page + 1;
    return dispatch(
      fetchFilterDimensionPage({
        dimension,
        page: nextPage,
        search: dimState.searchTerm || undefined,
      })
    );
  }
);

/**
 * Reset a dimension's items and fetch with a new search term.
 */
export const searchFilterDimension = createAsyncThunk(
  "filterDimensions/search",
  async ({ dimension, search }, { dispatch }) => {
    return dispatch(
      fetchFilterDimensionPage({ dimension, page: 1, search: search || undefined })
    );
  }
);

/**
 * Fetch the two-level segment hierarchy for the current scope.
 *
 * Only the hierarchical segment filter reads this. A scope the segmenter does not cover
 * returns parents with no children, which that filter renders as today's flat list.
 */
const fetchSegmentTreeData = createAsyncThunk(
  "filterDimensions/fetchSegmentTree",
  async (arg, { getState, rejectWithValue }) => {
    const { meta } = getState().filterDimensions;

    try {
      return await fetchSegmentTree({
        id_client: meta.id_client,
        country: meta.country,
        refresh: meta.refresh,
        signal: arg?.signal,
      });
    } catch (err) {
      if (err.name === "AbortError" || err.code === "ERR_CANCELED") {
        return rejectWithValue({ aborted: true });
      }
      return rejectWithValue({ error: err.message });
    }
  }
);

/**
 * Fetch the client's sales regions and their territories for the current scope.
 *
 * Only a market with sales territories needs this, so the caller dispatches it for
 * those markets alone rather than on every initialization. The scope travels with
 * the result so a response for a previous workspace or country can be dropped.
 */
export const fetchTerritoryTreeData = createAsyncThunk(
  "filterDimensions/fetchTerritoryTree",
  async (arg, { getState, rejectWithValue }) => {
    const { meta } = getState().filterDimensions;
    try {
      const data = await fetchTerritoryTree({
        id_client: meta.id_client,
        country: meta.country,
        refresh: meta.refresh,
        signal: arg?.signal,
      });
      return { data, id_client: meta.id_client, country: meta.country };
    } catch (err) {
      if (err.name === "AbortError" || err.code === "ERR_CANCELED") {
        return rejectWithValue({ aborted: true });
      }
      return rejectWithValue({ error: err.message, id_client: meta.id_client, country: meta.country });
    }
  }
);

/**
 * App-level initialization: fire page-1 fetches for every dimension in
 * parallel. Each per-dimension endpoint returns its own paginated slice
 * (50 items for paginated dims, full set for static dims via limit=10000)
 * with an authoritative `has_more` flag, so LazyDimensionSelect's
 * scroll-to-load-more works as designed without dumping every option into
 * the client up front.
 */
export const initializeFilterDimensions = createAsyncThunk(
  "filterDimensions/initialize",
  async ({ id_client, country, refresh }, { dispatch }) => {
    const fetches = ALL_DIMENSIONS.map((dimension) =>
      dispatch(fetchFilterDimensionPage({ dimension, page: 1 }))
    );
    fetches.push(dispatch(fetchSegmentTreeData()));
    await Promise.allSettled(fetches);
    return { id_client, country, refresh };
  }
);

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const filterDimensionsSlice = createSlice({
  name: "filterDimensions",
  initialState,
  reducers: {
    /**
     * Set the current filter context (id_client, country, refresh).
     * Should be called before initializeFilterDimensions.
     */
    setFilterContext: (state, action) => {
      const { id_client, country, refresh } = action.payload;
      const changed =
        state.meta.id_client !== id_client ||
        state.meta.country !== country;

      state.meta.id_client = id_client;
      state.meta.country = country;
      if (refresh !== undefined) state.meta.refresh = refresh;

      // Reset all dimensions if context changed
      if (changed) {
        state.meta.initialized = false;
        // Drop the cached refresh from the previous (id_client, country)
        // pair. dws_pet_performance refreshes are per-(country, id_client),
        // so the latest for Asahi/AU (e.g. 202602) is meaningless for
        // ko_global/TW where 202602 has zero rows. Leaving it set causes
        // the next fetchFilterDimensionPage to send refresh=202602, which
        // the API runs verbatim and returns empty items — every per-dim
        // dropdown ends up empty after a workspace+country switch.
        // Clearing here lets _resolve_refresh on the API resolve fresh
        // for the new context.
        if (refresh === undefined) state.meta.refresh = null;
        ALL_DIMENSIONS.forEach((dim) => {
          state.dimensions[dim] = makeDimensionState();
        });
        // Sub-segments are per-(country, id_client) like every other dimension, so a
        // stale tree would offer the previous scope's children.
        state.segmentTree = { items: [], loading: false, loaded: false };
        state.territoryTree = { items: [], loading: false, loaded: false, error: null };
      }
    },

    /** Full reset (e.g., on logout or workspace switch). */
    resetFilterDimensions: (state) => {
      state.meta = initialState.meta;
      ALL_DIMENSIONS.forEach((dim) => {
        state.dimensions[dim] = makeDimensionState();
      });
      state.segmentTree = { items: [], loading: false, loaded: false };
      state.territoryTree = { items: [], loading: false, loaded: false, error: null };
    },
  },
  extraReducers: (builder) => {
    // fetchFilterDimensionPage
    builder
      .addCase(fetchFilterDimensionPage.pending, (state, action) => {
        const { dimension } = action.meta.arg;
        if (state.dimensions[dimension]) {
          state.dimensions[dimension].loading = true;
        }
      })
      .addCase(fetchFilterDimensionPage.fulfilled, (state, action) => {
        if (!action.payload) return; // dispatched from fetchMore guard
        const { dimension, page, data, id_client, country } = action.payload;
        const dim = state.dimensions[dimension];
        if (!dim) return;
        const stale =
          (id_client !== undefined && id_client !== state.meta.id_client) ||
          (country !== undefined && country !== state.meta.country);
        if (stale) return;

        dim.loading = false;
        dim.loaded = true;
        dim.hasMore = data.has_more ?? false;
        dim.page = page;

        if (page === 1) {
          // Initial load or search reset → replace items
          dim.items = data.items ?? [];
        } else {
          // Load more → append (deduplicate)
          const existing = new Set(dim.items);
          const newItems = (data.items ?? []).filter((x) => !existing.has(x));
          dim.items = [...dim.items, ...newItems];
        }

        // Capture feature flags from platforms endpoint
        if (dimension === "platforms" && data.image_audit_feature_enabled !== undefined) {
          state.meta.image_audit_feature_enabled = data.image_audit_feature_enabled;
          if (data.refresh) state.meta.refresh = data.refresh;
        }
      })
      .addCase(fetchFilterDimensionPage.rejected, (state, action) => {
        const payload = action.payload;
        if (payload?.aborted) return; // ignore aborts
        const dimension = payload?.dimension ?? action.meta.arg?.dimension;
        if (dimension && state.dimensions[dimension]) {
          state.dimensions[dimension].loading = false;
        }
      });

    // fetchSegmentTreeData
    builder
      .addCase(fetchSegmentTreeData.pending, (state) => {
        state.segmentTree.loading = true;
      })
      .addCase(fetchSegmentTreeData.fulfilled, (state, action) => {
        state.segmentTree.loading = false;
        state.segmentTree.loaded = true;
        state.segmentTree.items = action.payload?.items ?? [];
      })
      .addCase(fetchSegmentTreeData.rejected, (state, action) => {
        if (action.payload?.aborted) return; // ignore aborts
        state.segmentTree.loading = false;
        // `loaded` stays false so the filter falls back to the flat segment_types list
        // rather than rendering an empty tree.
        state.segmentTree.items = [];
      });

    // fetchTerritoryTreeData — a result for another scope is dropped, so a slow
    // response cannot show one market's territories in the next.
    const isCurrentScope = (state, payload) =>
      payload?.id_client === state.meta.id_client && payload?.country === state.meta.country;
    builder
      .addCase(fetchTerritoryTreeData.pending, (state) => {
        state.territoryTree.loading = true;
        state.territoryTree.error = null;
      })
      .addCase(fetchTerritoryTreeData.fulfilled, (state, action) => {
        if (!isCurrentScope(state, action.payload)) return;
        state.territoryTree.loading = false;
        state.territoryTree.loaded = true;
        state.territoryTree.items = action.payload.data?.items ?? [];
      })
      .addCase(fetchTerritoryTreeData.rejected, (state, action) => {
        if (action.payload?.aborted || !isCurrentScope(state, action.payload)) return;
        state.territoryTree.loading = false;
        state.territoryTree.error = action.payload?.error ?? "Request failed";
      });

    // initializeFilterDimensions — items/hasMore/loading are managed by the
    // per-dimension fetchFilterDimensionPage reducer above. The outer thunk
    // only marks the slice as initialized once the parallel fetches settle.
    builder.addCase(initializeFilterDimensions.fulfilled, (state) => {
      state.meta.initialized = true;
    });
  },
});

export const { setFilterContext, resetFilterDimensions } =
  filterDimensionsSlice.actions;

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

export const selectFilterMeta = (state) => state.filterDimensions.meta;
export const selectImageAuditEnabled = (state) =>
  state.filterDimensions.meta.image_audit_feature_enabled;
export const selectFilterDimension = (dimension) => (state) =>
  state.filterDimensions.dimensions[dimension];
export const selectFilterItems = (dimension) => (state) =>
  state.filterDimensions.dimensions[dimension]?.items ?? [];
export const selectFilterInitialized = (state) =>
  state.filterDimensions.meta.initialized;

// ---------------------------------------------------------------------------
// Compatibility selector — maps new slice data to the {label, value}[] format
// consumed by existing filter components (useHomePage, FilterModal, etc.)
// Brand tree fields (is_offering_brands_tree, etc.) are NOT covered here —
// they still come from filtersSlice via fetchAllSubbrandsTree.
// ---------------------------------------------------------------------------

const PRICE_LABEL = (level) => {
  const map = { 0: "N/A", 1: "$", 2: "$$", 3: "$$$", 4: "$$$$" };
  return level >= 5 ? "$$$$+" : map[level] ?? String(level);
};


/**
 * Stable selector for the 3 truly static dimensions used directly in table column
 * definitions (platform, price_level, segment_type).
 *
 * Intentionally excludes all lazy-loaded dimensions (franchises, geo_level_1/2,
 * cities, cuisines, postal_codes, place_ids, crm_client_ids) so that loading
 * more items in those filters does NOT cause the column array to regenerate
 * (which would close any open AntD filter dropdown).
 */
export const selectColumnFilterOptions = createSelector(
  (state) => state.filterDimensions.dimensions.platforms?.items,
  (state) => state.filterDimensions.dimensions.price_levels?.items,
  (state) => state.filterDimensions.dimensions.segment_types?.items,
  (platformItems, priceLevelItems, segmentTypeItems) => {
    const seen = new Set();
    const priceLevelOptions = (priceLevelItems ?? []).flatMap((level) => {
      const value = level >= 5 ? 5 : level;
      if (seen.has(value)) return [];
      seen.add(value);
      const map = { 0: "N/A", 1: "$", 2: "$$", 3: "$$$", 4: "$$$$" };
      const label = level >= 5 ? "$$$$+" : map[level] ?? String(level);
      return [{ label, value }];
    });

    return {
      platform: (platformItems ?? []).map((v) => ({
        label: v.charAt(0).toUpperCase() + v.slice(1),
        value: v,
      })),
      price_level: priceLevelOptions,
      segment_type: (segmentTypeItems ?? []).map((v) => ({
        label: segmentLabel(v),
        value: v,
      })),
    };
  }
);

/** Returns filter options in {label, value}[] format for standard dimensions. */
export const selectFilterTextOptions = createSelector(
  (state) => state.filterDimensions.dimensions,
  (state) => state.filterDimensions.segmentTree.items,
  (state) => state.filterDimensions.territoryTree.items,
  (dims, segmentTreeItems, territoryTreeItems) => {
    const toOptions = (items) =>
      (items ?? []).map((v) => ({ label: v, value: v }));

    // price_levels needs dedup after value normalisation (5+ all map to 5)
    const seen = new Set();
    const priceLevelOptions = (dims.price_levels?.items ?? []).flatMap((level) => {
      const value = level >= 5 ? 5 : level;
      if (seen.has(value)) return [];
      seen.add(value);
      return [{ label: PRICE_LABEL(level), value }];
    });

    return {
      cuisine: toOptions(dims.cuisines?.items),
      platform: (dims.platforms?.items ?? []).map((v) => ({
        label: v.charAt(0).toUpperCase() + v.slice(1),
        value: v,
      })),
      franchise: toOptions(dims.franchises?.items),
      geo_level_1: toOptions(dims.geo_level_1?.items),
      geo_level_2: toOptions(dims.geo_level_2?.items),
      city: toOptions(dims.cities?.items),
      postal_code: toOptions(dims.postal_codes?.items),
      price_level: priceLevelOptions,
      segment_type: (dims.segment_types?.items ?? []).map((v) => ({
        label: segmentLabel(v),
        value: v,
      })),
      place_id: toOptions(dims.place_ids?.items),
      crm_client_id: toOptions(dims.crm_client_ids?.items),
      // Every node of the territory tree as a flat {label, value}, so a surface that
      // renders a saved view's filters can name a tree selection without the tree.
      territory_nodes: (territoryTreeItems ?? []).flatMap(({ sales_region, territories }) => [
        { label: sales_region, value: encodeTerritoryNode(sales_region) },
        ...(territories ?? [])
          .filter((t) => t !== sales_region)
          .map((territory) => ({
            label: `${sales_region} \u203a ${territory}`,
            value: encodeTerritoryNode(sales_region, territory),
          })),
      ]),
      // Every node of the segment tree as a flat {label, value}, for the same reason.
      segment_nodes: (segmentTreeItems ?? []).flatMap(
        ({ segment_type, sub_segments }) => [
          {
            label: segmentLabel(segment_type),
            value: encodeSegmentNode(segment_type),
          },
          ...(sub_segments ?? []).map((sub) => ({
            label: `${segmentLabel(segment_type)} \u203a ${sub}`,
            value: encodeSegmentNode(segment_type, sub),
          })),
        ]
      ),
    };
  }
);

/**
 * Segment hierarchy as AntD `Tree` nodes: a parent per segment type, children beneath.
 *
 * A parent carrying sub-segments also gets `childCount`, which the widget shows on
 * the row. A parent whose outlets carry no sub-segment gets neither, so the widget
 * draws it as a leaf. That is what every market the segmenter does not cover
 * looks like, and it renders as today's flat list of segment types.
 */
export const selectSegmentTreeData = createSelector(
  (state) => state.filterDimensions.segmentTree.items,
  (items) =>
    (items ?? []).map(({ segment_type, sub_segments }) => {
      const node = {
        key: encodeSegmentNode(segment_type),
        title: segmentLabel(segment_type),
        value: segment_type,
      };
      if (sub_segments?.length) {
        // The count is what tells a reader there is something to expand. A
        // caret alone is a design hint; a number is information, and a parent
        // with no children shows none, which teaches the difference.
        node.childCount = sub_segments.length;
        node.children = sub_segments.map((sub) => ({
          key: encodeSegmentNode(segment_type, sub),
          title: subSegmentLabel(sub),
          value: sub,
        }));
      }
      return node;
    })
);

/** The territory tree's request state, for the loading, empty and error views. */
export const selectTerritoryTreeState = (state) => state.filterDimensions.territoryTree;

/**
 * The territory tree as AntD `Tree` nodes: a region per parent, its territories beneath.
 *
 * A region whose only territory carries the region's own name, which is how
 * 'Unassigned' arrives, draws as a leaf, because a child repeating its parent says
 * nothing.
 */
export const selectTerritoryTreeData = createSelector(
  (state) => state.filterDimensions.territoryTree.items,
  (items) =>
    (items ?? []).map(({ sales_region, territories }) => {
      const node = { key: encodeTerritoryNode(sales_region), title: sales_region, value: sales_region };
      const children = (territories ?? []).filter((t) => t !== sales_region);
      if (children.length) {
        node.childCount = children.length;
        node.children = children.map((territory) => ({
          key: encodeTerritoryNode(sales_region, territory),
          title: territory,
          value: territory,
        }));
      }
      return node;
    })
);

export default filterDimensionsSlice.reducer;
